import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import EmailIcon from '@mui/icons-material/Email';
import SendIcon from '@mui/icons-material/Send';

interface Props {
  open: boolean;
  onClose: () => void;
  /** Código de factura o cotización (p.ej. FA-000001 / COT-000001) */
  codigoDocumento: string;
  tipoDocumento: 'factura' | 'cotizacion';
  /** Correo ya registrado del cliente — puede ser vacío */
  correoCliente?: string;
  /** Callback que ejecuta el envío; recibe el email final */
  onEnviar: (emailDestino: string) => Promise<void>;
  darkMode?: boolean;
}

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export const EnviarEmailModal: React.FC<Props> = ({
  open,
  onClose,
  codigoDocumento,
  tipoDocumento,
  correoCliente,
  onEnviar,
  darkMode = false,
}) => {
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [enviado, setEnviado] = useState(false);

  // Pre-llenar con el correo del cliente si existe
  useEffect(() => {
    if (open) {
      setEmail(correoCliente || '');
      setError('');
      setEnviado(false);
    }
  }, [open, correoCliente]);

  const handleSubmit = async () => {
    const emailFinal = email.trim();
    if (!EMAIL_REGEX.test(emailFinal)) {
      setError('Ingrese un correo electrónico válido');
      return;
    }
    setError('');
    setLoading(true);
    try {
      await onEnviar(emailFinal);
      setEnviado(true);
    } catch (err: any) {
      setError(err.message || 'No se pudo enviar el correo');
    } finally {
      setLoading(false);
    }
  };

  const titulo = tipoDocumento === 'factura' ? 'Enviar Factura por Correo' : 'Enviar Cotización por Correo';
  const etiqueta = tipoDocumento === 'factura' ? 'factura' : 'cotización';

  return (
    <Dialog
      open={open}
      onClose={!loading ? onClose : undefined}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '12px',
          bgcolor: darkMode ? '#202020' : '#fff',
          color: darkMode ? '#e8e8e6' : '#37352f',
        },
      }}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box
            sx={{
              width: 36, height: 36, borderRadius: '8px',
              bgcolor: darkMode ? '#143a52' : '#e8f0fe',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}
          >
            <EmailIcon sx={{ fontSize: 20, color: darkMode ? '#529cca' : '#2383e2' }} />
          </Box>
          <Typography sx={{ fontWeight: 700, fontSize: '1rem' }}>{titulo}</Typography>
        </Box>
        <IconButton onClick={onClose} disabled={loading} size="small">
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ pt: 1 }}>
        {enviado ? (
          <Box sx={{ py: 2, textAlign: 'center' }}>
            <Typography sx={{ color: '#0f7b6c', fontWeight: 600, fontSize: '0.95rem' }}>
              ✓ {tipoDocumento === 'factura' ? 'Factura' : 'Cotización'} enviada correctamente a:
            </Typography>
            <Typography sx={{ mt: 0.5, fontWeight: 500 }}>{email}</Typography>
          </Box>
        ) : (
          <>
            <Typography sx={{ fontSize: '0.85rem', color: darkMode ? '#9b9a97' : '#6b6b6b', mb: 2 }}>
              Se enviará la {etiqueta} <strong>{codigoDocumento}</strong> al siguiente correo electrónico.
            </Typography>

            {correoCliente && (
              <Typography sx={{ fontSize: '0.78rem', color: darkMode ? '#4dab9a' : '#0f7b6c', mb: 1.5 }}>
                Correo registrado del cliente detectado.
              </Typography>
            )}

            <TextField
              label="Correo electrónico"
              value={email}
              onChange={(e) => { setEmail(e.target.value); setError(''); }}
              onKeyDown={(e) => { if (e.key === 'Enter') handleSubmit(); }}
              fullWidth
              size="small"
              error={!!error}
              helperText={error || (!correoCliente ? 'El cliente no tiene correo registrado. Ingrese uno manualmente.' : '')}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <EmailIcon sx={{ fontSize: 18, color: darkMode ? '#9b9a97' : '#6b6b6b' }} />
                  </InputAdornment>
                ),
              }}
              sx={{
                '& .MuiInputBase-root': {
                  borderRadius: '8px',
                  bgcolor: darkMode ? '#2b2b2b' : '#f7f7f5',
                },
              }}
            />
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
        <Button
          onClick={onClose}
          disabled={loading}
          sx={{ borderRadius: '8px', color: darkMode ? '#9b9a97' : '#6b6b6b', textTransform: 'none' }}
        >
          {enviado ? 'Cerrar' : 'Cancelar'}
        </Button>
        {!enviado && (
          <Button
            onClick={handleSubmit}
            disabled={loading || !email.trim()}
            variant="contained"
            disableElevation
            startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <SendIcon />}
            sx={{
              borderRadius: '8px',
              bgcolor: darkMode ? '#529cca' : '#2383e2',
              textTransform: 'none',
              fontWeight: 600,
              '&:hover': { bgcolor: darkMode ? '#6bafdb' : '#1b6fc2' },
            }}
          >
            {loading ? 'Enviando…' : 'Enviar'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};
